/**
 * Grounded Yard tab — types + pure helpers for the per-driver leg stats
 * (GET /driver-dispatch/grounded-yard-stats). Kept DOM-free so the column
 * logic and footer totals are unit-testable.
 *
 * Design: docs/plans/2026-09-08-grounded-yard-tab-stats-design.md
 */

export interface GroundedYardLeg {
  pronumber: number;
  legnum: number;
  orig?: string;
  dest: string;
  status: string; // NOT STARTED | STARTED | IN TRANSIT | ARRIVED | COMPLETE
}

export interface GroundedYardDecline {
  pronumber: number;
  rejectdatetime: string | null;
  rejectreason: string;
  rejectcomment: string;
  orig: string;
  dest: string;
}

export interface GroundedYardDriverStats {
  driver: string;
  started: boolean;
  started_at: string | null;
  cy_picked: number;
  cy_planned: number;
  cy_legs: GroundedYardLeg[];
  open_legs: GroundedYardLeg[];
  declined: GroundedYardDecline[];
}

export interface GroundedYardTotals {
  drivers: number;
  started: number;
  cy_picked: number;
  cy_planned: number;
  open_legs: number;
  declined: number;
}

export interface GroundedYardStatsResponse {
  date: string;
  stats_available: boolean;
  yard_labels: string[];
  drivers: Record<string, GroundedYardDriverStats>;
  totals: GroundedYardTotals;
}

export type StartedState = 'started' | 'flag-only' | 'not-started';

/** started = first activity time known; flag-only = driver tapped Start in
 *  the app (or a stamp exists on another day) but nothing timed today. */
export function startedState(s: GroundedYardDriverStats | undefined | null): StartedState {
  if (!s || !s.started) return 'not-started';
  return s.started_at ? 'started' : 'flag-only';
}

/** "2026-09-04T06:42:00" | "2026-09-04 06:42:00" -> "06:42". The stamp is
 *  DB wall-clock (Central) — no timezone conversion. */
export function formatStartedTime(iso: string | null): string {
  if (!iso) return '';
  const m = /(\d{2}):(\d{2})/.exec(iso.slice(10));
  return m ? `${m[1]}:${m[2]}` : '';
}

/** Ascending sort: earliest start first, then flag-only, then not started. */
export function startedSortKey(s: GroundedYardDriverStats | undefined | null): string {
  switch (startedState(s)) {
    case 'started':
      return `0:${s!.started_at}`;
    case 'flag-only':
      return '1';
    default:
      return '2';
  }
}

/** Footer totals over the rows actually on screen (after dispatcher/region
 *  filters). A driver with no stats row still counts as a driver. */
export function summarizeVisible(
  rows: ReadonlyArray<GroundedYardDriverStats | undefined | null>
): GroundedYardTotals {
  const totals: GroundedYardTotals = {
    drivers: 0, started: 0, cy_picked: 0, cy_planned: 0, open_legs: 0, declined: 0,
  };
  for (const s of rows) {
    totals.drivers += 1;
    if (!s) continue;
    if (s.started) totals.started += 1;
    totals.cy_picked += s.cy_picked;
    totals.cy_planned += s.cy_planned;
    totals.open_legs += s.open_legs.length;
    totals.declined += s.declined.length;
  }
  return totals;
}
